import { type FC } from 'react';
import { useSelector } from 'react-redux';

import { ChangesStatus } from '@editor2/types/changesStatus';
import { type RootState } from '@editor2/reducers/root';
import { chapterToChapterProperties } from '@editor2/mappers/chapter';
import ChangesHeader from './common/ChangesHeader';
import AttributesChanges from './common/AttributesChanges';
import ChildrenChanges from './common/ChildrenChanges';

interface Props {
	courseId: number;
	chapterId: string;
}

const ChapterChanges: FC<Props> = (props) => {
	const { chapterId, courseId } = props;

	const { changes, chapters, initialCourse } = useSelector(
		(state: RootState) => state.courses[courseId]
	);

	const changesStatuses = changes.chapters[chapterId];
	const currentChapter = chapters.entities[chapterId];
	const initialChapter = initialCourse.chapters.entities[chapterId];

	const getChapterNumber = (chapterId: string) => {
		const initChapter = initialCourse.chapters.entities[chapterId];
		const chapter = chapters.entities[chapterId];

		const deleted = changes.chapters[chapterId].includes(ChangesStatus.Deleted);

		switch (true) {
			case chapter && chapter.chapter_number && chapter.chapter_number !== ' ':
				return chapter.chapter_number;
			case deleted && initChapter.chapter_number && initChapter.chapter_number !== ' ':
				return initChapter.chapter_number;
			default: {
				const entity = chapter || initChapter;
				return entity.name;
			}
		}
	};

	const getChapterLink = () => {
		return `/courses/${courseId}/chapters/${chapterId}/`;
	};

	return (
		<div className="changeBlock">
			<ChangesHeader
				changesStatuses={changesStatuses}
				headerTitle={`Chapter ${getChapterNumber(chapterId)}`}
				link={changesStatuses.includes(ChangesStatus.Deleted) ? null : getChapterLink()}
			/>

			{changesStatuses.includes(ChangesStatus.Modified) &&
				!changesStatuses.includes(ChangesStatus.New) && (
					<AttributesChanges
						currentEntity={currentChapter}
						initialEntity={initialChapter}
						mapper={chapterToChapterProperties}
					/>
				)}

			{changesStatuses.includes(ChangesStatus.ChildrenReorder) &&
				currentChapter.page_groups_enabled && (
					<ChildrenChanges
						currentIds={currentChapter.page_groups}
						initialIds={initialChapter?.page_groups || []}
						changesEntity={changes.page_groups}
						title="page group"
					/>
				)}

			{changesStatuses.includes(ChangesStatus.ChildrenReorder) &&
				!currentChapter.page_groups_enabled && (
					<ChildrenChanges
						currentIds={currentChapter.pages}
						initialIds={initialChapter?.pages || []}
						changesEntity={changes.pages}
						title="page"
					/>
				)}
		</div>
	);
};

export default ChapterChanges;
